import is from '@sindresorhus/is';
import { first } from 'lodash';
import type { ReactNode } from 'react';
import { useContext, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { Badge } from '~/ui/core/badge/Badge';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

const formatName = (version: JobProfileVersion): string => {
    if (!is.nullOrUndefined(version.createdBy)) {
        return `${version.createdBy?.firstName} ${version.createdBy?.lastName}`;
    }

    if (is.nullOrUndefined(version.reviewer)) {
        return '';
    }

    return `${version.reviewer?.firstName} ${version.reviewer?.lastName}`;
};

export const useFormattedVersions = (
    versions: readonly JobProfileVersion[],
): {
    readonly formattedVersions: readonly VersionSelect[];
    readonly newTmpVersion: VersionSelect;
} => {
    const { translate } = useContext(ApplicationContext);

    const newTmpVersion: VersionSelect = {
        id: ((first(versions?.map((version) => version?.version)) ?? 0) + 1).toString(),
        author: 'New Version',
        badge: <Badge>{translate('pages.job-architecture-v2.review-state.draft')}</Badge>,
        title: translate('pages.job-architecture-v2.job-profile-version', {
            version: ((first(versions)?.version ?? 0) + 1).toString(),
        }),
    };

    return {
        formattedVersions: useMemo(() => {
            const getBadge = (version: JobProfileVersion): ReactNode => {
                if (version.publishedAt !== null) {
                    return (
                        <Badge variant="success">{translate('pages.job-architecture-v2.review-state.published')}</Badge>
                    );
                }

                if (version.reviewedAt !== null) {
                    return <Badge variant="orange">{translate('pages.job-architecture-v2.job-roles-reviewed')}</Badge>;
                }

                return <Badge variant="gray">{translate('pages.job-architecture-v2.review-state.draft')}</Badge>;
            };

            return versions.map((version) => ({
                id: version.version.toString(),
                author: formatName(version),
                badge: getBadge(version),
                title: translate('pages.job-architecture-v2.job-profile-version', {
                    version: version.version.toString(),
                }),
            }));
        }, [versions, translate]),
        newTmpVersion,
    };
};
